import type { MdmMap } from "./mdm";
import type { ArenaMap, MatchRules, MatchState, PlayerCount, PlayerInput, PlayerProfile, PlayerSlot } from "./types";

export const PROTOCOL_VERSION = 1;

export interface AccountProfile extends PlayerProfile {
  readonly username: string;
}

export interface IceServerConfig {
  readonly urls: string | readonly string[];
  readonly username?: string;
  readonly credential?: string;
}

export interface LobbyPlayer {
  readonly peerId: string;
  readonly slot: PlayerSlot;
  readonly profile: PlayerProfile;
  readonly ready: boolean;
  readonly connected: boolean;
}

export interface LobbySettings {
  readonly maxPlayers: PlayerCount;
  readonly mapId: string;
  readonly map: ArenaMap;
  readonly rules: MatchRules;
}

export type ClientMessage =
  | { readonly type: "hello"; readonly token?: string; readonly language: "cz" | "en"; readonly profile: PlayerProfile }
  | { readonly type: "createRoom"; readonly settings: LobbySettings }
  | { readonly type: "joinRoom"; readonly code: string }
  | { readonly type: "leaveRoom" }
  | { readonly type: "setReady"; readonly ready: boolean }
  | { readonly type: "updateProfile"; readonly profile: PlayerProfile }
  | { readonly type: "updateSettings"; readonly settings: LobbySettings }
  | { readonly type: "startRoom" }
  | { readonly type: "signal"; readonly targetPeerId: string; readonly description?: RTCSessionDescriptionInit; readonly candidate?: RTCIceCandidateInit }
  | { readonly type: "ping"; readonly sentAt: number };

export type ServerMessage =
  | { readonly type: "welcome"; readonly peerId: string; readonly accountId: string | null }
  | { readonly type: "roomCreated"; readonly code: string }
  | { readonly type: "roomState"; readonly code: string; readonly hostPeerId: string; readonly players: readonly LobbyPlayer[]; readonly settings: LobbySettings }
  | { readonly type: "matched"; readonly matchId: string; readonly hostPeerId: string; readonly ownSlot: PlayerSlot; readonly peers: readonly LobbyPlayer[]; readonly settings: LobbySettings; readonly iceServers: readonly IceServerConfig[]; readonly seed: number }
  | { readonly type: "signal"; readonly fromPeerId: string; readonly description?: RTCSessionDescriptionInit; readonly candidate?: RTCIceCandidateInit }
  | { readonly type: "peerLeft"; readonly peerId: string; readonly hostLeft: boolean }
  | { readonly type: "pong"; readonly sentAt: number }
  | { readonly type: "error"; readonly code: string };

export type PeerMessage =
  | { readonly type: "input"; readonly sequence: number; readonly tick: number; readonly slot: PlayerSlot; readonly input: PlayerInput }
  | { readonly type: "snapshot"; readonly sequence: number; readonly acknowledgedInputs: Readonly<Partial<Record<PlayerSlot, number>>>; readonly state: MatchState }
  | { readonly type: "event"; readonly sequence: number; readonly events: MatchState["events"] }
  | { readonly type: "resyncRequest"; readonly lastSnapshot: number }
  | { readonly type: "leave" };

export interface MapLibraryRecord {
  readonly id: string;
  readonly authorId: string;
  readonly authorName: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string;
  readonly language: "cz" | "en";
  readonly playerCount: PlayerCount;
  readonly schemaVersion: 1;
  readonly map: MdmMap;
  readonly published: boolean;
  readonly plays: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}
